import React, { useState, useRef, useEffect } from 'react';
import Icon from '../../../components/AppIcon';

import Button from '../../../components/ui/Button';

const CameraCapture = ({ images, onImagesChange, primaryImageIndex, onPrimaryImageChange }) => {
  const [isCameraActive, setIsCameraActive] = useState(false);
  const [stream, setStream] = useState(null);
  const [facingMode, setFacingMode] = useState('environment');
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  const streamRef = useRef(null);

  const startCamera = async () => {
    try {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }

      const mediaStream = await navigator.mediaDevices?.getUserMedia({
        video: { facingMode: facingMode, width: { ideal: 1920 }, height: { ideal: 1080 } },
        audio: false
      });

      streamRef.current = mediaStream;
      setStream(mediaStream);
      setIsCameraActive(true);
    } catch (error) {
      console.error('Camera access error:', error);
      alert('Unable to access camera. Please check permissions.');
    }
  };

  useEffect(() => {
    if (stream && videoRef.current) {
      videoRef.current.srcObject = stream;
      videoRef.current.play().catch(e => console.error('Error playing video:', e));
    }
  }, [stream, isCameraActive]);

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track?.stop());
      streamRef.current = null;
    }
    setStream(null);
    setIsCameraActive(false);
  };

  const switchCamera = () => {
    stopCamera();
    setFacingMode(prev => prev === 'user' ? 'environment' : 'user');
  };

  useEffect(() => {
    if (facingMode && !isCameraActive) {
      return;
    }
    if (facingMode) {
      startCamera();
    }
  }, [facingMode]);

  const capturePhoto = () => {
    if (images?.length >= 12) {
      alert('Maximum 12 images allowed per item');
      return;
    }

    const video = videoRef?.current;
    const canvas = canvasRef?.current;

    if (video && canvas) {
      canvas.width = video?.videoWidth;
      canvas.height = video?.videoHeight;
      const context = canvas?.getContext('2d');
      context?.drawImage(video, 0, 0);

      canvas?.toBlob((blob) => {
        const imageUrl = URL.createObjectURL(blob);
        const newImages = [...images, { url: imageUrl, blob: blob, timestamp: new Date()?.toISOString() }];
        onImagesChange(newImages);

        if (newImages?.length === 1) {
          onPrimaryImageChange(0);
        }
      }, 'image/jpeg', 0.95);
    }
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e?.target?.files);
    const remainingSlots = 12 - images?.length;

    if (files?.length > remainingSlots) {
      alert(`You can only add ${remainingSlots} more image(s)`);
      return;
    }

    const newImages = [...images];

    for (const file of files) {
      try {
        const { compressImage } = await import('../../../utils/imageUtils');
        const compressedFile = await compressImage(file, 1920, 0.85); // Compress down to max 1920px width
        const imageUrl = URL.createObjectURL(compressedFile);
        newImages.push({ url: imageUrl, blob: compressedFile, timestamp: new Date().toISOString() });
      } catch (err) {
        console.error('Failed to compress image:', err);
        const imageUrl = URL.createObjectURL(file);
        newImages.push({ url: imageUrl, blob: file, timestamp: new Date().toISOString() });
      }
    }

    onImagesChange(newImages);

    if (images?.length === 0 && newImages?.length > 0) {
      onPrimaryImageChange(0);
    }

    e.target.value = ''; // Reset so the identical file can be picked again if needed
  };

  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track?.stop());
        streamRef.current = null;
      }
    };
  }, []);

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-foreground">Capture Images</h2>
        <span className="text-sm text-muted-foreground">{images?.length}/12 images</span>
      </div>
      {!isCameraActive ? (
        <div className="space-y-4">
          <div className="aspect-video bg-muted rounded-lg flex flex-col items-center justify-center gap-4 border-2 border-dashed border-border">
            <Icon name="Camera" size={48} color="var(--color-muted-foreground)" />
            <p className="text-sm text-muted-foreground">Camera ready to capture</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Button
              variant="default"
              onClick={startCamera}
              iconName="Camera"
              iconPosition="left"
              fullWidth
              disabled={images?.length >= 12}
            >
              Start Camera
            </Button>

            <label className="w-full">
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handleFileUpload}
                className="hidden"
                disabled={images?.length >= 12}
              />
              <Button
                variant="outline"
                iconName="Upload"
                iconPosition="left"
                fullWidth
                disabled={images?.length >= 12}
                asChild
              >
                <span>Upload Images</span>
              </Button>
            </label>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="relative aspect-video bg-background rounded-lg overflow-hidden border border-border">
            <video
              ref={videoRef}
              autoPlay
              playsInline
              className="w-full h-full object-cover"
            />
            <canvas ref={canvasRef} className="hidden" />

            <div className="absolute bottom-4 left-0 right-0 flex items-center justify-center gap-4">
              <Button
                variant="secondary"
                size="icon"
                onClick={switchCamera}
                iconName="RefreshCw"
                iconSize={20}
              >
                <span className="sr-only">Switch camera</span>
              </Button>

              <Button
                variant="default"
                onClick={capturePhoto}
                className="w-16 h-16 flex items-center justify-center rounded-full p-0"
                title="Capture photo"
              >
                <Icon name="Camera" size={28} />
                <span className="sr-only">Capture photo</span>
              </Button>

              <Button
                variant="destructive"
                size="icon"
                onClick={stopCamera}
                iconName="X"
                iconSize={20}
              >
                <span className="sr-only">Close camera</span>
              </Button>
            </div>
          </div>
        </div>
      )}
      {images?.length > 0 && (
        <div className="mt-4 p-4 bg-success/5 rounded-lg border border-success/20">
          <div className="flex items-center gap-2">
            <Icon name="CheckCircle2" size={18} color="var(--color-success)" />
            <span className="text-sm font-medium text-success">
              {images?.length} image{images?.length !== 1 ? 's' : ''} captured
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default CameraCapture;